import { Box } from '@material-ui/core'
import { Button } from 'components/facets'
import { useUser } from 'components/providers/UserProvider'
import { getPriceFormat } from 'lib/utils'
import moment from 'moment'
import { useState } from 'react'
import { CSVLink } from 'react-csv'
import { useTranslation } from 'react-i18next'
import {
  CardLanguage,
  escapeDoubleQuotes,
  getDisplayLocationName,
  getIssues,
  getOrderState,
  Location,
  Order,
  OrderArticle,
  OrderIssue,
} from 'shared'
interface ExportOrdersProps {
  orders: Order[]
  filteredOrders: Order[]
}

interface CsvOrderItem {
  idOrder: string
  customer: string
  status: string
  dateBought: Date | undefined
  datePaid: Date | undefined
  dateSent: Date | undefined
  dateReceived: Date | undefined
  shippingMethod: string | undefined
  trackingNumber: string
  temporaryEmail: string
  isPresale: boolean
  shippingAddressName: string | undefined
  shippingAddressExtra: string | undefined
  shippingAddressStreet: string | undefined
  shippingAddressCity: string | undefined
  shippingAddressZip: string | undefined
  shippingAddressCountry: string | undefined
  phone: string | undefined
  buyerVAT: string | undefined
  updatedAt: string
  articleCategories: string
  articles: number
  articleValue: number
  totalValue: number
  currencyCode: string
  note: string
  // PID: string | undefined
  issues: string | undefined
}

interface CsvOrderArticleItem {
  idOrder: string
  customer: string | undefined
  status: string | undefined
  pos: string | undefined
  items: number
  category: string
  expansion: string
  name: string
  language: string
  finishType: string
  isSigned: boolean
  isPlayset: boolean
  isAltered: boolean
  condition: string
  price: string
  comments: string
  issues: string | undefined
  location: string
  boughtAt: Date | undefined
  cardmarketId: number | undefined
}

export const ExportOrders = ({ orders, filteredOrders }: ExportOrdersProps): JSX.Element => {
  const { user } = useUser()
  const { t } = useTranslation()
  const [orderData, setOrderData] = useState<CsvOrderItem[]>([])
  const [articleData, setArticleData] = useState<CsvOrderArticleItem[]>([])

  const fileFormats = ['csv', 'xlsx']
  // const [fileFormat, setFileFormat] = useState(fileFormats[0])
  const fileFormat = fileFormats[0]

  const getTextIssues = (issues: OrderIssue[]) => {
    const textIssues = issues.map((i) => i.description)
    return textIssues.join('\n')
  }

  const getPhoneNumber = (o: Order) => {
    const phoneNumber = o.buyer?.phone
    if (phoneNumber) {
      return `'${phoneNumber}'`
    } else {
      return ''
    }
  }

  const getRowFromOrder = (o: Order) => {
    return {
      idOrder: o.sourceOrderId,
      customer: o.buyer
        ? o.buyer.username
          ? `${o.buyer.firstName} ${o.buyer.lastName ?? ''}(${o.buyer.username})`
          : `${o.buyer.firstName} ${o.buyer.lastName ?? ''}`
        : 'Unknown',
      status: getOrderState(o),
      dateBought: o.state.dateBought ?? undefined,
      datePaid: o.state.datePaid,
      dateSent: o.state.dateSent,
      dateReceived: o.state.dateReceived,
      shippingMethod: o.shipping?.carrier,
      trackingNumber: o.shipping?.tracking ?? '',
      temporaryEmail: o.buyer?.email ?? '',
      isPresale: o.isPresale,
      shippingAddressName: o.shipping?.name,
      shippingAddressExtra: escapeDoubleQuotes(o.shipping?.extra),
      shippingAddressStreet: o.shipping?.street,
      shippingAddressCity: o.shipping?.city,
      shippingAddressZip: o.shipping?.zip,
      shippingAddressCountry: o.shipping?.country,
      phone: getPhoneNumber(o),
      buyerVAT: o.buyer?.vat,
      updatedAt: moment(o.updatedAt).format('DD.MM.YYYY HH:mm'),
      articleCategories: o.articleCategories?.join(', '),
      articles: o.articlesCount,
      articleValue: o.articleValue,
      totalValue: o.totalValue,
      currencyCode: o.currency,
      note: o.note ?? '',
      PID: o.picking?.pickingId,
      issues: getTextIssues(getIssues(o)),
    }
  }

  const handleOrdersDownload = (done: (proceed: boolean | undefined) => void): void => {
    setOrderData(
      orders.map((o) => {
        const row: CsvOrderItem = getRowFromOrder(o)
        return row
      })
    )
    if (window.Cypress) done(false)
    else done(true)
  }

  const handleFilteredOrdersDownload = (done: (proceed: boolean | undefined) => void): void => {
    setOrderData(
      filteredOrders.map((o) => {
        const row: CsvOrderItem = getRowFromOrder(o)
        return row
      })
    )
    if (window.Cypress) done(false)
    else done(true)
  }

  const handleArticlesDownload = (done: (proceed: boolean | undefined) => void): void => {
    const articles: OrderArticle[] = []
    orders.forEach((order) => {
      articles.push(...(order.articles ?? []))
    })

    const articleData = articles.reduce((acc, a) => {
      if (!a.price) {
        console.error('Article has no price', a)
      }

      const rows: CsvOrderArticleItem[] = a.locations.map((location: Location) => ({
        idOrder: a.sourceOrderId,
        customer: a.buyer?.username,
        status: a.state?.state,
        pos: a.pos,
        items: a.quantity,
        category: '',
        expansion: escapeDoubleQuotes(a.card.expansion),
        name: escapeDoubleQuotes(a.getLocalizedName()),
        language: CardLanguage[a.idLanguage],
        condition: a.condition,
        price: getPriceFormat(a.price, a.currencyCode),
        comments: a.comment,
        finishType: a.finishType,
        isSigned: a.isSigned,
        isPlayset: a.isPlayset,
        isAltered: a.isAltered,
        issues: getTextIssues(a.issues),
        location: JSON.stringify({
          locationName: getDisplayLocationName(location.name),
          locationQuantity: location.quantity,
        }),
        boughtAt: a.state?.dateBought,
        cardmarketId: a.idProduct,
      }))

      return acc.concat(rows)
    }, [] as CsvOrderArticleItem[])

    setArticleData(articleData)

    if (window.Cypress) done(false)
    else done(true)
  }

  const fileName = `${user?.cardmarketUsername}-${moment(Date.now()).format(
    'DD-MM-YYYY_HH-mm'
  )}.${fileFormat}`

  // const handleChangeFileFormat = (e: any) => {
  //   setFileFormat(e.target.value)
  // }

  return (
    <>
      {/* <FormLabel>
        <Typography>{t('orders.chooseFormat')}</Typography>
      </FormLabel>
      <RadioGroup value={fileFormat} onChange={handleChangeFileFormat}>
        <Box display="flex">
          {fileFormats.map((f) => (
            <FormControlLabel key={f} value={f} control={<Radio />} label={f} />
          ))}
        </Box>
      </RadioGroup> */}
      <Box marginY={2}>
        <CSVLink
          data={orderData}
          asyncOnClick={true}
          onClick={(_event, done) => {
            handleOrdersDownload(done)
          }}
          enclosingCharacter={`"`}
          filename={`orders-${fileName}`}
          style={{ textDecoration: 'none' }}
        >
          <Button fullWidth variant="contained" color="primary">
            {t('orders.downloadOrders')}
          </Button>
        </CSVLink>
      </Box>

      <Box marginY={2}>
        <CSVLink
          data={orderData}
          asyncOnClick={true}
          onClick={(_event, done) => {
            handleFilteredOrdersDownload(done)
          }}
          enclosingCharacter={`"`}
          filename={`filteredOrders-${fileName}`}
          style={{ textDecoration: 'none' }}
        >
          <Button fullWidth variant="contained" color="primary">
            {t('orders.downloadFilteredOrders')}
          </Button>
        </CSVLink>
      </Box>

      <Box marginTop={1}>
        <CSVLink
          data={articleData}
          asyncOnClick={true}
          onClick={(_event, done) => {
            handleArticlesDownload(done)
          }}
          enclosingCharacter={`"`}
          filename={`articles-${fileName}`}
          style={{ textDecoration: 'none' }}
        >
          <Button fullWidth variant="contained" color="primary">
            {t('orders.downloadArticles')}
          </Button>
        </CSVLink>
      </Box>
    </>
  )
}
